import React, { Component } from 'react';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import i18n from '@i18next/i18n';
import PlayingContainer from '@src/page/toc/common/meeting-room-manager/components/new-playing-container';
import { checkIfLoadJsSdk } from '@src/slide/infrastructure/module/independent/wechat-helper';
import { setDocCanEditInTencentMeeting } from '@utils/tencent-meeting';
import NotPlayingContainerSlide from '../../components/not-playing-container/v2';
import NotPlayingContainerTDoc from '../../components/not-playing-container';
import MeetingRoomReport from '../../utils/report';
import {
    MEETING_SCENE_SOURCE,
    ROLE,
    DOC_TYPE,
    MEETING_ROOM_STATE,
    MEETING_ROOM_ACTION,
} from '@src/page/toc/common/meeting-room-manager/message_const';
import { changeFavicon } from '../../../favicon-manager';
import { getRoomInfo, checkType } from '../../server/api';
import { setTitleButtons, parseResponse, openVConsoleInDevMobile } from '../../utils';
import * as icons from '../../img/svg';
import stylesMobile from '../../style-mobile.less';
import stylesPC from '../../style-pc.less';
import { CUSTOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { isMeetingRoomDataReady } from './meeting-room-utils';
import { IMeetingRoomStates, IMeetingRoomProps, MeetingRoomEvents } from './meeting-room-interface';

const styles = browser.isMobile ? stylesMobile : stylesPC;
/**
 * 待机界面展示信息
 */
export const LIVE_INFO_MAP = {
    [DOC_TYPE.SLIDE]: {
        typeName: i18n.t('幻灯片'),
        favicon: CUSTOM.FAVICON.SLIDE,
        logo: icons.PPT_LOGO,
    },
    [DOC_TYPE.DOC]: {
        typeName: i18n.t('文档'),
        favicon: CUSTOM.FAVICON.WORD,
        logo: icons.DOC_LOGO,
    },
    [DOC_TYPE.PDF]: {
        typeName: i18n.t('PDF'),
        favicon: CUSTOM.FAVICON.PDF,
        logo: icons.PDF_LOGO,
    },
    [DOC_TYPE.UNKNOWN]: {
        typeName: i18n.t('腾讯文档'),
        favicon: CUSTOM.FAVICON.SLIDE,
        logo: icons.LOGO,
    },
};

export default class MeetingRoom extends Component<IMeetingRoomProps, IMeetingRoomStates> {
    private meetingRoomReport = MeetingRoomReport.getInstance();
    private sceneSource: MEETING_SCENE_SOURCE;
    private roomId: string;
    private docId: string;
    private docType: DOC_TYPE;
    private role: ROLE;

    // 状态机转换关系(当前状态，动作 -> 下一状态)，可能有简化的空间
    private MeetingRoomStateMap = new Map([
        [`${MEETING_ROOM_STATE.INIT_LOADING}${MEETING_ROOM_ACTION.ROOM_READY}`, MEETING_ROOM_STATE.PLAY],
        [`${MEETING_ROOM_STATE.INIT_LOADING}${MEETING_ROOM_ACTION.AUDIENCE_WAIT}`, MEETING_ROOM_STATE.AUDIENCE_WAIT],
        [`${MEETING_ROOM_STATE.AUDIENCE_WAIT}${MEETING_ROOM_ACTION.ROOM_READY}`, MEETING_ROOM_STATE.PLAY],
        [`${MEETING_ROOM_STATE.PLAY}${MEETING_ROOM_ACTION.AUDIENCE_WAIT}`, MEETING_ROOM_STATE.AUDIENCE_WAIT],
    ]);

    public constructor(props: IMeetingRoomProps) {
        super(props);
        this.state = {
            meetingRoomState: MEETING_ROOM_STATE.INIT_LOADING,
            notPlayingContainerData: {
                docTitle: i18n.t('腾讯文档'),
                lastError: '',
            },
            padId: '',
            dataReady: isMeetingRoomDataReady(props.initData),
        };
        const { sceneSource, roomId, docId, docType, role } = props.initData;
        this.sceneSource = sceneSource;
        this.roomId = roomId;
        this.docId = docId;
        this.docType = docType;
        this.role = role;

        // 修改图标
        changeFavicon(LIVE_INFO_MAP[this.docType].favicon);
        // 腾讯会议场景下告知客户端当前不处于编辑态
        setDocCanEditInTencentMeeting(false);
        // 移动端开发环境下展示控制台
        openVConsoleInDevMobile();
        // 加载jssdk，提供和微信、qq的交互支持
        if (browser.isMobile) {
            checkIfLoadJsSdk();
        }
        setTitleButtons();
    }

    public componentDidMount(): void {
        const { dataReady } = this.state;
        if (!dataReady) {
            // 在数据不完备的时候进入 error 状态
            // 之后可以考虑内部主动再次获取数据
            this.changeRoomState(MEETING_ROOM_ACTION.MEETING_ERROR, i18n.t('数据有误'));
            return;
        }

        // 数据校验补充
        // 1.检查房间是否关闭
        if (this.role === ROLE.AUDIENCE) {
            getRoomInfo(this.roomId).then((docId) => {
                if (!docId) {
                    this.changeRoomState(MEETING_ROOM_ACTION.MEETING_CLOSE);
                }
            });
        }
        /**
         * 2.获取 padId，为了腾讯会议下点击退出演示的时候能够设置权限
         * 获取 docTitle，做 UI 显示
         */
        checkType(this.docId).then((response) => {
            // 企微未登录
            if (response?.data?.retcode === 11000) {
                this.changeRoomState(MEETING_ROOM_ACTION.MEETING_ERROR, i18n.t('用户未登录'));
            }
            const docInfo = parseResponse(response);
            const { padId, docTitle } = docInfo;
            this.setState({
                padId,
                notPlayingContainerData: {
                    ...this.state.notPlayingContainerData,
                    docTitle,
                },
            });
        });
    }

    public render(): JSX.Element {
        const { meetingRoomState, notPlayingContainerData } = this.state;
        const { useIframePlayer } = this.props;

        return (
            <div className={styles['manager-wrapper']}>
                {meetingRoomState !== MEETING_ROOM_STATE.CLOSED && (
                    <div className={'wrapperStyle'} id="player">
                        <div className={styles.playerFrameContainer}>{this.getPlayer()}</div>
                    </div>
                )}
                {useIframePlayer ? (
                    <NotPlayingContainerTDoc
                        meetingRoomState={meetingRoomState}
                        logo={LIVE_INFO_MAP[this.docType].logo}
                        typeName={LIVE_INFO_MAP[this.docType].typeName}
                        lastError={notPlayingContainerData.lastError}
                        docTitle={notPlayingContainerData.docTitle}
                        isMaster={this.role === ROLE.MASTER}
                    />
                ) : (
                    <NotPlayingContainerSlide
                        meetingRoomState={meetingRoomState}
                        logo={LIVE_INFO_MAP[this.docType].logo}
                        typeName={LIVE_INFO_MAP[this.docType].typeName}
                        lastError={notPlayingContainerData.lastError}
                        docTitle={notPlayingContainerData.docTitle}
                        isMaster={this.role === ROLE.MASTER}
                        loadingText={notPlayingContainerData.loadingText}
                    />
                )}
            </div>
        );
    }

    /**
     * 获取播放器
     */
    private getPlayer() {
        const { dataReady } = this.state;
        if (!dataReady) {
            return <></>;
        }
        return (
            <>
                {this.props.children}
                {this.getPlayingContainer()}
            </>
        );
    }

    // 房间状态改变，依赖状态机
    private changeRoomState = <T extends MEETING_ROOM_ACTION>(
        action: T,
        ...addition: Parameters<MeetingRoomEvents[T]>
    ) => {
        this.onBeforeChangeState(action, ...addition);
        const state = this.state.meetingRoomState;
        const nextState = this.changeToNextState(state, action);
        console.log('[remote meeting room]', `use action: ${action}, meeting room state from ${state} to ${nextState}`);
        this.setState({
            meetingRoomState: nextState,
        });
    };

    /**
     * 房间状态改变之前：修改UI文案
     */
    private onBeforeChangeState = <T extends MEETING_ROOM_ACTION>(
        action: T,
        ...addition: Parameters<MeetingRoomEvents[T]>
    ) => {
        if (action === MEETING_ROOM_ACTION.MEETING_CLOSE) {
            this.setState({
                notPlayingContainerData: {
                    ...this.state.notPlayingContainerData,
                    loadingText: undefined,
                },
            });
            return;
        }
        if (action === MEETING_ROOM_ACTION.MEETING_ERROR) {
            this.setState({
                notPlayingContainerData: {
                    ...this.state.notPlayingContainerData,
                    lastError: addition[0] as Parameters<MeetingRoomEvents[MEETING_ROOM_ACTION.MEETING_ERROR]>[0],
                },
            });
            return;
        }
    };

    private changeToNextState = (state: MEETING_ROOM_STATE, action: MEETING_ROOM_ACTION) => {
        if (action === MEETING_ROOM_ACTION.MEETING_CLOSE) {
            return MEETING_ROOM_STATE.CLOSED;
        }
        if (action === MEETING_ROOM_ACTION.MEETING_ERROR) {
            return MEETING_ROOM_STATE.ERROR_PAGE;
        }
        return this.MeetingRoomStateMap.get(`${state}${action}`) ?? state;
    };

    private showRoomError = (error: string) => {
        this.changeRoomState(MEETING_ROOM_ACTION.MEETING_ERROR, error);
    };

    private changeLoadingText = (showLoading: boolean, loadingText?: string) => {
        if (!showLoading) {
            this.setState({
                notPlayingContainerData: {
                    ...this.state.notPlayingContainerData,
                    loadingText: undefined,
                },
            });
            return;
        }
        this.setState({
            notPlayingContainerData: {
                ...this.state.notPlayingContainerData,
                loadingText,
            },
        });
    };

    private getPlayingContainer = () => {
        const { padId, meetingRoomState } = this.state;
        const { currentPage, handleEndPlay } = this.props;
        return (
            <PlayingContainer
                role={this.role}
                docId={this.docId}
                docType={this.docType}
                roomId={this.roomId}
                padId={padId}
                currentPage={currentPage ?? 0}
                playerShow={meetingRoomState === MEETING_ROOM_STATE.PLAY}
                handleScenceEditorEndPlay={handleEndPlay}
                scenceEditorRemote={this.sceneSource === MEETING_SCENE_SOURCE.InEditor}
                sceneSource={this.sceneSource}
                meetingRoomStateChange={this.changeRoomState}
                changeLoadingText={this.changeLoadingText}
                showRoomClose={() => this.changeRoomState(MEETING_ROOM_ACTION.MEETING_CLOSE)}
                showRoomError={this.showRoomError}
                meetingRoomReport={this.meetingRoomReport}
                liveManager={this.props.liveManager}
            />
        );
    };
}
